import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  Grid,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { makeStyles } from '@mui/styles';
import { CustomModal, FormButton } from '../../../../../components';
import * as UserActions from '../../../../../core/users/action';

function VendorProfileFormModal({ isOpen, handleClose }) {
  const useStyles = makeStyles({
    input: {
      background: 'white',
    },
  });
  const classes = useStyles();
  const { user, tagsList, loading, userEdited } = useSelector((store) => store.users);
  const dispatch = useDispatch();
  const [selectedTags, setSelectedTags] = useState([]);
  const [files, setFiles] = useState([]);
  const updateFiles = (incommingFiles) => {
    setFiles(incommingFiles);
  };

  useEffect(() => {
    dispatch(UserActions.getTags());
  }, [dispatch]);

  const getUserTags = useCallback(() => {
    const values = user.user_tags.map((ut) => ut.category.name);
    setSelectedTags(() => values);
  }, [user]);

  useEffect(() => {
    getUserTags();
  }, [getUserTags]);

  const schemeEditProfile = yup.object({
    companyName: yup.string().required('Required'),
    businessDesc: yup.string().required('Required'),
    productDesc: yup.string().required('Required'),
    categories: yup.array().min(1, 'At least one tag must be selected'),
    webSite: yup
      .string()
      .matches(
        /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
        'Enter correct url!',
      ),
    social_network: yup.array().of(
      yup.object().shape({
        url: yup
          .string()
          .matches(/^(https?:\/\/)?(www\.)?(facebook|instagram)\.com\/.+$/, 'Invalid URL'),
      }),
    ),
  });
  const formikEditVendorProfile = useFormik({
    enableReinitialize: true,
    initialValues: {
      companyName: user.vendor_company_name ? user.vendor_company_name : '',
      businessDesc: user.vendor_business_desc ? user.vendor_business_desc : '',
      webSite: user.web_site ? user.web_site : '',
      social_network: user.vendor_social_network ? user.vendor_social_network : '',
      productDesc: user.vendor_product_desc ? user.vendor_product_desc : '',
      categories: user.user_tags ? user.user_tags.map((ut) => ut.category.name) : [],
    },
    validationSchema: schemeEditProfile,
    onSubmit: async (values, errors) => {
      try {
        console.log('🍉 Saving vendor info ', values);
        const request = {
          id: user.id,
          vendor_business_desc: values.businessDesc,
          vendor_company_name: values.companyName,
          vendor_product_desc: values.productDesc,
          web_site: values.webSite,
          user_tags: values.categories.map((tag) => ({
            fk_user: user.id,
            fk_category: tagsList.filter((tag2) => tag2.name === tag)[0].id,
          })),
          vendor_social_network: Array.isArray(values.social_network)
            ? values.social_network.map((sn) => ({
                id: sn.id,
                name: sn.name,
                url: sn.url,
                fk_vendor: user.id,
              }))
            : [],
        };
        console.log('🍉 Before dispatch ');
        dispatch(UserActions.editUser(request));
        handleClose();
      } catch (err) {
        console.log('🍉 Edit vendor error  ', err);
      }
    },
  });
  const renderSocialNetworkFields = () => {
    let socialNetwork = formikEditVendorProfile.values.social_network;
    if (socialNetwork.length <= 0) {
      socialNetwork = [
        {
          name: 'Facebook',
          url: '',
          fk_venue: null,
          fk_vendor: user.id,
        },
        {
          name: 'Instagram',
          url: '',
          fk_venue: null,
          fk_vendor: user.user,
        },
      ];
    }
    return socialNetwork.map((network, index) => {
      const handleUrlChange = (e) => {
        const updatedNetworks = [...socialNetwork];
        updatedNetworks[index].url = e.target.value;
        formikEditVendorProfile.setFieldValue('social_network', updatedNetworks);
      };

      return (
        <div key={network.id}>
          <TextField
            placeholder={`${network.name} Url`}
            value={network.url}
            onChange={handleUrlChange}
            inputProps={{
              className: classes.input,
              maxLength: 100,
            }}
            style={{ marginTop: '5px' }}
            fullWidth
            error={
              formikEditVendorProfile.touched.social_network &&
              formikEditVendorProfile.errors.social_network &&
              formikEditVendorProfile.errors.social_network[index]?.url
            }
            helperText={
              formikEditVendorProfile.touched.social_network &&
              formikEditVendorProfile.errors.social_network &&
              formikEditVendorProfile.errors.social_network[index]?.url?.replace(
                /social_network\[\d+\]\.url\s+/,
                '',
              )
            }
          />
        </div>
      );
    });
  };
  return (
    <CustomModal isOpen={isOpen} handleClose={handleClose}>
      <Grid container justifyContent="center" rowGap={1}>
        <Grid item xs={12}>
          <IconButton onClick={handleClose}>
            <CancelOutlinedIcon sx={{ fill: '#0997ba' }} />
          </IconButton>
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            Edit your profile
          </Typography>
        </Grid>
        <Grid item xs={12} mt={2}>
          <form style={{ width: '100%' }} onSubmit={formikEditVendorProfile.handleSubmit}>
            <TextField
              inputProps={{
                className: classes.input,
                maxLength: 100,
              }}
              style={{ marginTop: '5px' }}
              fullWidth
              id="companyName"
              name="companyName"
              placeholder="Company name"
              value={formikEditVendorProfile.values.companyName}
              onChange={formikEditVendorProfile.handleChange}
              error={
                formikEditVendorProfile.touched.companyName &&
                Boolean(formikEditVendorProfile.errors.companyName)
              }
              helperText={
                formikEditVendorProfile.errors.companyName &&
                formikEditVendorProfile.errors.companyName
              }
            />
            <TextField
              inputProps={{
                className: classes.input,
                maxLength: 300,
              }}
              style={{ marginTop: '5px' }}
              fullWidth
              id="businessDesc"
              name="businessDesc"
              multiline
              rows={3}
              placeholder="My business summary"
              value={formikEditVendorProfile.values.businessDesc}
              onChange={formikEditVendorProfile.handleChange}
              error={
                formikEditVendorProfile.touched.businessDesc &&
                Boolean(formikEditVendorProfile.errors.businessDesc)
              }
              helperText={
                formikEditVendorProfile.errors.businessDesc &&
                formikEditVendorProfile.errors.businessDesc
              }
            />
            <TextField
              inputProps={{
                className: classes.input,
                maxLength: 300,
              }}
              style={{ marginTop: '5px' }}
              fullWidth
              id="productDesc"
              name="productDesc"
              multiline
              rows={3}
              placeholder="Product's description"
              value={formikEditVendorProfile.values.productDesc}
              onChange={formikEditVendorProfile.handleChange}
              error={
                formikEditVendorProfile.touched.productDesc &&
                Boolean(formikEditVendorProfile.errors.productDesc)
              }
              helperText={
                formikEditVendorProfile.errors.productDesc &&
                formikEditVendorProfile.errors.productDesc
              }
            />
            <Typography variant="body1" sx={{ mt: 1 }}>
              Choosing between 3-5 categories will help you find the best venue matches
            </Typography>
            <Select
              multiple
              style={{ marginTop: '5px' }}
              fullWidth
              displayEmpty
              id="categories"
              name="categories"
              value={selectedTags}
              onChange={(selected) => {
                const {
                  target: { value },
                } = selected;
                setSelectedTags(
                  // On autofill we get a stringified value.
                  // eslint-disable-next-line no-constant-condition
                  typeof value === 'string' ? value.split(',') : value,
                );
                formikEditVendorProfile.setFieldValue('categories', value);
              }}
              input={<OutlinedInput />}
              error={
                formikEditVendorProfile.touched.categories &&
                Boolean(formikEditVendorProfile.errors.categories)
              }
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Categories</em>;
                }

                return selected.join(', ');
              }}>
              <MenuItem disabled value="">
                <em>Categories</em>
              </MenuItem>
              {tagsList &&
                tagsList.length > 0 &&
                tagsList.map((tag) => (
                  <MenuItem key={tag.id} value={tag.name}>
                    {tag.name}
                  </MenuItem>
                ))}
            </Select>
            <TextField
              inputProps={{
                className: classes.input,
                maxLength: 100,
              }}
              style={{ marginTop: '5px' }}
              fullWidth
              id="webSite"
              name="webSite"
              placeholder="Web site"
              value={formikEditVendorProfile.values.webSite}
              onChange={formikEditVendorProfile.handleChange}
              error={
                formikEditVendorProfile.touched.webSite &&
                Boolean(formikEditVendorProfile.errors.webSite)
              }
              helperText={
                formikEditVendorProfile.errors.webSite && formikEditVendorProfile.errors.webSite
              }
            />
            {renderSocialNetworkFields()}
            <Grid item xs={12} className="grid-space-line" />
            <FormButton type="submit">Update</FormButton>
          </form>
        </Grid>
      </Grid>
    </CustomModal>
  );
}

VendorProfileFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default VendorProfileFormModal;
